<template>
    <div class="flex flex-col gap-1">
        <label
            v-if="label"
            class="mb-0 text-content-md font-medium text-content-secondary"
            :class="{ required }"
            :for="id"
        >
            <slot name="label">
                <span class="whitespace-nowrap">
                    {{ label }}
                </span>
            </slot>
        </label>
        <div
            class="ds-phone-input"
            :class="{ 'has-error': hasError }"
            data-testid="phone-input-container"
        >
            <DsMenu
                v-model:open="isFlagSelectorOpen"
                align="start"
                class="max-h-80"
                :disabled
                side="bottom"
                :sideOffset="8"
            >
                <template #menuTrigger>
                    <DsButton
                        class="ds-phone-flag-selector"
                        :class="{ 'is-open': isFlagSelectorOpen }"
                        data-testid="flag-selector-button"
                        :disabled
                        size="md"
                        variant="icon"
                    >
                        <span class="flex items-center gap-2">
                            <CountryFlag
                                :countryName="internalCountryCode.name"
                                data-testid="country-flag"
                                :iso2="internalCountryCode.iso2"
                            />
                            <DsIcon
                                icon="fa-solid fa-chevron-down"
                                size="sm"
                            />
                        </span>
                    </DsButton>
                </template>

                <DsMenuGroup>
                    <DsMenuOption
                        v-for="option in preferredCountriesOptions"
                        :id="option.iso2"
                        :key="option.iso2"
                        @click="handleCountryChange(option)"
                    >
                        <template #leading>
                            <CountryFlag
                                :countryName="option.name"
                                :iso2="option.iso2"
                            />
                        </template>
                        <span class="text-content-primary">
                            {{ option.name }}
                        </span>
                        <template #trailing>
                            <span class="text-content-md text-content-secondary">+{{ option.dialCode }}</span>
                        </template>
                    </DsMenuOption>
                </DsMenuGroup>

                <DsMenuOption
                    v-for="option in remainingCountriesOptions"
                    :id="option.iso2"
                    :key="option.iso2"
                    @click="handleCountryChange(option)"
                >
                    <template #leading>
                        <CountryFlag
                            :countryName="option.name"
                            :iso2="option.iso2"
                        />
                    </template>
                    <span class="text-content-primary">
                        {{ option.name }}
                    </span>
                    <template #trailing>
                        <span class="text-content-md text-content-secondary">+{{ option.dialCode }}</span>
                    </template>
                </DsMenuOption>
            </DsMenu>

            <div class="min-w-0 flex-1">
                <DsTextInput
                    v-model="phoneNumberModel"
                    ref="phoneInputRef"
                    data-testid="phone-number-input"
                    :disabled
                    :list
                    :name
                    type="tel"
                    @keypress="rejectNonNumeric"
                    @update:isFocused="handleTextFieldFocusOut"
                />
            </div>
        </div>
        <div
            v-if="hasError || helperText"
            class="text-content-md font-normal text-content-secondary"
            data-testid="helper-text-container"
        >
            <div
                v-if="hasError"
                class="flex flex-row items-center gap-2 text-content-error"
                data-testid="error-message"
            >
                <DsIcon
                    icon="fa-solid fa-triangle-exclamation"
                    size="sm"
                />
                <slot name="error">
                    {{ error }}
                </slot>
            </div>
            <div v-else-if="helperText">{{ helperText }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { removeNonNumeric } from '@/shared/utils/format-number';
import {
    formatE164ToNational,
    getAllSupportedCountries,
    parseAsYouType,
    parseInternationalNumber,
} from '@/shared/utils/phone-number';
import DsIcon from '@ds/DsIcon.vue';
import CountryFlag from '@ds/form/input/DsPhoneInput/CountryFlag.vue';
import { type Country, type DsPhoneInputProps } from '@ds/form/input/DsPhoneInput/phone-input.types';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import DsMenu from '@ds/menu/DsMenu.vue';
import DsMenuGroup from '@ds/menu/DsMenuGroup.vue';
import DsMenuOption from '@ds/menu/DsMenuOption.vue';
import { getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js';
import { computed, onMounted, ref, shallowRef, useId, useTemplateRef, watch, watchEffect } from 'vue';

const NUMERIC_REGEX = /^\D$/;

const props = withDefaults(defineProps<DsPhoneInputProps>(), {
    id: () => useId(),
    disabled: false,
    preferredCountries: () => ['US', 'CA', 'GB'],
    defaultCountry: () => ({
        iso2: 'US',
        name: 'United States',
        dialCode: getCountryCallingCode('US'),
    }),
});

const emit = defineEmits<{
    countryChanged: [country: Country];
    validityChanged: [boolean];
}>();

const phoneNumber = defineModel<string>();

const hasError = computed(() => !!props.error);

const internalDisplayValue = ref('');
const internalCountryCode = ref<Country>(props.defaultCountry);
const isFlagSelectorOpen = ref(false);
const phoneInputRef = useTemplateRef('phoneInputRef');

const countryOptions = shallowRef(getAllSupportedCountries());

const preferredCountriesSet = computed(() => new Set(props.preferredCountries));

const clearPhoneNumber = () => {
    internalDisplayValue.value = '';
    phoneNumber.value = '';
};

const initializePhoneNumber = () => {
    if (!phoneNumber.value) return;

    if (phoneNumber.value.startsWith('+')) {
        const parsed = parseInternationalNumber(phoneNumber.value);
        if (parsed) {
            internalCountryCode.value = parsed;
            emit('countryChanged', parsed);
        }
    }

    internalDisplayValue.value = formatE164ToNational(phoneNumber.value, internalCountryCode.value.iso2);
};

const phoneNumberModel = computed({
    get(): string {
        return internalDisplayValue.value;
    },
    set(displayValue: string) {
        const numericOnly = displayValue ? removeNonNumeric(displayValue) : '';
        if (!numericOnly) {
            clearPhoneNumber();
            return;
        }

        const result = parseAsYouType(numericOnly, internalCountryCode.value.iso2);
        internalDisplayValue.value = result.formatted || numericOnly;
        phoneNumber.value = result.e164;
    },
});

const preferredCountriesOptions = computed(() =>
    countryOptions.value.filter(option => preferredCountriesSet.value.has(option.iso2)),
);

const remainingCountriesOptions = computed(() =>
    countryOptions.value.filter(option => !preferredCountriesSet.value.has(option.iso2)),
);

const rejectNonNumeric = (event: KeyboardEvent) => {
    if (NUMERIC_REGEX.test(event.key)) {
        event.preventDefault();
    }
};

const handleCountryChange = (country: Country) => {
    if (props.disabled) return;

    const previousCountry = internalCountryCode.value;
    internalCountryCode.value = country;
    emit('countryChanged', country);

    if (internalDisplayValue.value && previousCountry.iso2 !== country.iso2) {
        const numericOnly = removeNonNumeric(internalDisplayValue.value);
        const result = parseAsYouType(numericOnly, country.iso2);
        phoneNumber.value = result.e164;
        internalDisplayValue.value = formatE164ToNational(result.e164, country.iso2);
    }

    isFlagSelectorOpen.value = false;
    phoneInputRef.value?.inputElement?.focus();
};

onMounted(() => {
    initializePhoneNumber();
});

watchEffect(() => {
    emit('validityChanged', isValidPhoneNumber(phoneNumber.value ?? '', internalCountryCode.value.iso2));
});

watch(phoneNumber, newPhoneNumber => {
    if (!newPhoneNumber) {
        internalDisplayValue.value = '';
        return;
    }

    const currentE164 = internalDisplayValue.value
        ? parseAsYouType(removeNonNumeric(internalDisplayValue.value), internalCountryCode.value.iso2).e164
        : '';

    // The input already reflects this value (e.g. the user just typed it); nothing to sync.
    if (newPhoneNumber === currentE164) return;

    // A programmatic model change can carry a different country than the cached one,
    // so re-derive the country before formatting to keep the flag and input coherent.
    const parsedInternational = parseInternationalNumber(newPhoneNumber);
    if (parsedInternational && parsedInternational.iso2 !== internalCountryCode.value.iso2) {
        internalCountryCode.value = parsedInternational;
        emit('countryChanged', parsedInternational);
    }

    internalDisplayValue.value = formatE164ToNational(newPhoneNumber, internalCountryCode.value.iso2);
});

const handleTextFieldFocusOut = (isFocused?: boolean) => {
    if (!isFocused) {
        internalDisplayValue.value = formatE164ToNational(internalDisplayValue.value, internalCountryCode.value.iso2);
    }
};

defineExpose({ initializePhoneNumber });
</script>

<style scoped lang="scss">
$flag-selector-layer: 1;
$base-layer: 0;

.ds-phone-input {
    @apply flex flex-1 items-center;

    &.has-error {
        .ds-phone-flag-selector,
        :deep(.ds-text-input),
        :deep(.ds-text-input .ds-input-inner-wrapper) {
            @apply outline-2 -outline-offset-0 outline-ds-red-600;
        }
    }

    .ds-phone-flag-selector {
        @apply relative flex items-center gap-2 rounded-e-none bg-primary p-3 text-content-lg leading-none text-content-primary outline outline-1 -outline-offset-[1px] outline-ds-primary-400;
        margin-right: -1px; // Collapse the shared border

        &:hover {
            @apply bg-secondary;
        }

        &:disabled {
            @apply cursor-not-allowed bg-ds-gray-100 text-content-disabled;
        }

        &:focus-within,
        &.is-open {
            @apply shadow-outline-field outline-transparent;
            outline: 2px solid theme('colors.ds.gray.800');
            outline-offset: 0;
            z-index: $flag-selector-layer;
        }
    }

    :deep(.ds-text-input),
    :deep(.ds-text-input .ds-input-inner-wrapper) {
        @apply rounded-s-none;
        z-index: $base-layer;
    }
}

:deep(.required) {
    &::after {
        content: '*';
        @apply text-content-error;
    }
}
</style>
